import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { MapPin, MapPinned, MapPinPlus, UserPlus, UserRoundPlus, Users } from "lucide-react";
import BaseSelect from "../../Base/BaseSelect";
import MultiSelectSearchable from "../../Base/MultiSelectSearchable";
import BaseButton from "../../Base/BaseButton";
import { Service } from "../../../models/ServiceModel";
import { Passenger } from "../../../models/PassengerModel";
import PassengerServicesTable, {
  PassengerRow,
} from "../tables/Services/PassengerServicesTable";
import showToast from "../../../utils/toast";
import useServicesService from "../../../services/useServicesService";
import usePassengerService from "../../../services/usePassengerService ";
import { isEnaexCompany } from "../../../config/adicionalCompany";
import BulkPassengerModal, {
  BulkPassengerCreated,
} from "../../Passengers/BulkPassengerModal";
import PassengerForm from "../../Passengers/PassengerForm";
import ButtonCancelForm from "../../forms/ButtonCancelForm";
import ButtonSaveForm from "../../forms/ButtonSaveForm";
import { useFormErrors } from "../../../hooks/useFormErrors";
import {
  isPlaceholderAddress,
  normalizeStreet,
  pickPreferredAddress,
} from "../../Passengers/bulkPassengerValidation";
import LocationQuickAddModal, {
  QuickCreatedLocation,
} from "../../Locations/LocationQuickAddModal";
import "../styles/ServiceFormStep2.css";

type SameSlotConflict = {
  id: number;
  origin: string;
  destination: string;
  passengers: string;
};

type CodeConflict = SameSlotConflict & { code: string };

interface Props {
  value: Service;
  onChange: (field: keyof Service, value: any) => void;
  passengers: any[];
  onAddSequence: (seq: any[]) => void;
  locations: { value: string; label: string }[];
  /** En edición, excluye el propio servicio de la coincidencia por hora. */
  excludeServiceId?: number;
  /** Nombre de la empresa seleccionada (paso 1), para reglas ENAEX. */
  companyName?: string | null;
  /** Tras alta masiva, el padre actualiza la lista del select de pasajeros. */
  onPassengersCreated?: (created: BulkPassengerCreated[]) => void;
  /** Tras editar un pasajero desde la tabla de ruta. */
  onPassengerUpdated?: (updated: {
    id: number;
    name: string;
    addresses: { id: number; street: string }[];
  }) => void;
}

const MAX_BULK_PASSENGERS = 7;

const withSequentialOrder = <T extends { order?: number }>(rows: T[]): T[] =>
  rows.map((row, index) => ({ ...row, order: index + 1 }));

function passengerLabel(p: {
  name?: string | null;
  last_name?: string | null;
  first_lastname?: string | null;
  second_lastname?: string | null;
}) {
  const first = p.first_lastname ?? p.last_name;
  if (first != null || p.second_lastname != null) {
    return [p.name, first, p.second_lastname]
      .map((part) => String(part ?? "").trim())
      .filter(Boolean)
      .join(" ");
  }
  return String(p.name ?? "").trim();
}

const ROUTE_INTRO: Record<string, string> = {
  DESTINO_DOMICILIOS:
    "Un origen fijo y, debajo, cada pasajero con su domicilio de destino.",
  ORIGEN_DOMICILIOS:
    "Pasajeros con domicilio de recogida y un único destino final.",
  MANUAL:
    "Origen y destino en catálogo; los pasajeros usan dirección manual.",
};

type LocationSelectCardProps = {
  name: "origin" | "destination";
  title: string;
  shortHint: string;
  icon: React.ReactNode;
  options: { value: string; label: string }[];
  value: string;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  /** Helper para crear una ubicación nueva sin salir del formulario. */
  onAddNew?: () => void;
};

const LocationSelectCard: React.FC<LocationSelectCardProps> = ({
  name,
  title,
  shortHint,
  icon,
  options,
  value,
  onChange,
  onAddNew,
}) => (
  <div className="svc-loc-card">
    <div className="svc-loc-card__head">
      <span className="svc-loc-card__icon-wrap" aria-hidden>
        {icon}
      </span>
      <div className="svc-loc-card__titles">
        <h4 className="svc-loc-card__title">{title}</h4>
        <p className="svc-loc-card__hint">{shortHint}</p>
      </div>
      {onAddNew && (
        <button
          type="button"
          className="svc-loc-card__add-btn"
          onClick={onAddNew}
          title="Crear una ubicación nueva en el catálogo"
        >
          <MapPinPlus size={16} strokeWidth={2.25} aria-hidden />
          Nueva
        </button>
      )}
    </div>
    <BaseSelect
      label="Ubicación"
      name={name}
      placeholder="Seleccionar ubicación"
      preserveCase
      options={options}
      value={value}
      onChange={onChange}
    />
  </div>
);

const ServiceFormStep2: React.FC<Props> = ({
  value,
  onChange,
  passengers,
  onAddSequence,
  locations,
  excludeServiceId,
  companyName,
  onPassengersCreated,
  onPassengerUpdated,
}) => {
  const { sameTimeServices, fetching } = useServicesService();
  const {
    edit: editPassenger,
    update: updatePassenger,
  } = usePassengerService();
  const { fieldErrors, clearErrors, setErrorsFromResponse } = useFormErrors();
  const [sameSlotConflicts, setSameSlotConflicts] = useState<SameSlotConflict[]>([]);
  const [codeConflicts, setCodeConflicts] = useState<CodeConflict[]>([]);
  const sameTimeAbortRef = useRef<AbortController | null>(null);
  const isEnaex = isEnaexCompany(companyName);
  const [serviceType, setServiceType] = useState("MANUAL");
  const [selectedPassengerIds, setSelectedPassengerIds] = useState<number[]>(
    []
  );
  const [selectedAddressId, setSelectedAddressId] = useState<number | null>(
    null
  );
  const [availableAddresses, setAvailableAddresses] = useState<
    { id: number; street: string }[]
  >([]);
  const [rows, setRows] = useState<any[]>([]);
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  /** A qué campo se asignará la ubicación creada con el helper. */
  const [locationModalFor, setLocationModalFor] = useState<"origin" | "destination" | null>(null);
  /** Ubicaciones creadas desde el helper, aún no presentes en la lista del padre. */
  const [extraLocations, setExtraLocations] = useState<{ value: string; label: string }[]>([]);
  const [editingPassenger, setEditingPassenger] = useState<Passenger | null>(null);
  const [editingRowIndex, setEditingRowIndex] = useState<number | null>(null);
  const [editCompanies, setEditCompanies] = useState<{ value: string; label: string }[]>([]);
  const [editSavingLocked, setEditSavingLocked] = useState(false);
  const isManual = serviceType === "MANUAL";
  const companyId = value.company;

  // Catálogo del padre + creadas localmente, sin duplicados y en orden alfabético
  const locationOptions = useMemo(() => {
    const byValue = new Map<string, { value: string; label: string }>();
    for (const opt of [...locations, ...extraLocations]) {
      byValue.set(String(opt.value), { value: String(opt.value), label: opt.label });
    }
    return Array.from(byValue.values()).sort((a, b) =>
      a.label.localeCompare(b.label, "es", { sensitivity: "base" })
    );
  }, [locations, extraLocations]);

  const handleLocationCreated = useCallback(
    (loc: QuickCreatedLocation) => {
      setExtraLocations((prev) =>
        prev.some((o) => o.value === String(loc.id))
          ? prev
          : [...prev, { value: String(loc.id), label: loc.name }]
      );
      if (locationModalFor) {
        onChange(locationModalFor, String(loc.id));
      }
      setLocationModalFor(null);
    },
    [locationModalFor, onChange]
  );

  const availableSlots = Math.max(0, MAX_BULK_PASSENGERS - rows.length);

  const passengerSelectOptions = useMemo(() => {
    const addedIds = new Set(rows.map((r) => r.id));
    return passengers
      .filter((p) => !addedIds.has(p.id))
      .map((p) => ({
        value: String(p.id),
        label: passengerLabel(p),
      }));
  }, [passengers, rows]);

  const singleSelectedPassengerId =
    selectedPassengerIds.length === 1 ? selectedPassengerIds[0] : null;

  useEffect(() => {
    const t = value.service_type != null ? String(value.service_type) : "MANUAL";
    setServiceType(t);
  }, [value.service_type]);

  useEffect(() => {
    const company = value.company;
    const date = value.service_date;
    const time = value.service_time;
    if (!company || !date || !String(time).trim()) {
      setSameSlotConflicts([]);
      setCodeConflicts([]);
      return;
    }

    const debounceMs = 400;
    const timer = window.setTimeout(() => {
      sameTimeAbortRef.current?.abort();
      const ctrl = new AbortController();
      sameTimeAbortRef.current = ctrl;

      const timeStr = String(time).trim().slice(0, 8);
      const codeTrim = String(value.enaex_code ?? "").trim();
      const params: {
        company_id: number;
        service_date: string;
        service_time: string;
        exclude_service_id?: number;
        service_code?: string;
      } = {
        company_id: Number(company),
        service_date: String(date).trim().slice(0, 10),
        service_time: timeStr,
        exclude_service_id: excludeServiceId,
      };
      if (isEnaex && codeTrim) {
        params.service_code = codeTrim;
      }

      sameTimeServices(
        params,
        {
          onSuccess: (r: any) => {
            const list = r?.data?.services;
            setSameSlotConflicts(Array.isArray(list) ? list : []);
            const cList = r?.data?.code_conflicts;
            setCodeConflicts(Array.isArray(cList) ? cList : []);
          },
          onError: () => {
            setSameSlotConflicts([]);
            setCodeConflicts([]);
          },
        },
        ctrl.signal
      );
    }, debounceMs);

    return () => {
      window.clearTimeout(timer);
      sameTimeAbortRef.current?.abort();
    };
  }, [
    value.company,
    value.service_date,
    value.service_time,
    value.enaex_code,
    excludeServiceId,
    sameTimeServices,
    isEnaex,
  ]);

  // Sincronizar rows con value.sequences cuando cambia desde el padre
  useEffect(() => {
    const sequences = value.sequences;
    if (sequences && Array.isArray(sequences)) {
      if (sequences.length > 0) {
        // Solo actualizar si las secuencias son diferentes
        const currentRowsStr = JSON.stringify(rows.map(r => ({ id: r.id, name: r.name, address: r.address })));
        const newSequencesStr = JSON.stringify(sequences.map((s: any) => ({ id: s.id, name: s.name, address: s.address })));
        if (currentRowsStr !== newSequencesStr) {
          setRows(sequences as any[]);
        }
      } else {
        setRows([]);
      }
    } else {
      setRows([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.sequences]);

  const handleServiceTypeChange = (newType: string) => {
    setServiceType(newType);
    onChange("service_type", newType);
    setRows([]);
    onAddSequence([]);
    setSelectedPassengerIds([]);
    setSelectedAddressId(null);
    setAvailableAddresses([]);
  };

  const handlePassengersChange = (ids: number[]) => {
    const nextIds = ids.slice(0, availableSlots);
    setSelectedPassengerIds(nextIds);
    if (nextIds.length === 1) {
      const passenger = passengers.find((p) => p.id === nextIds[0]);
      const addresses = passenger?.addresses ?? [];
      setAvailableAddresses(addresses);
      const firstAddressId =
        !isManual && addresses.length > 0 ? addresses[0].id : null;
      setSelectedAddressId(firstAddressId);
    } else {
      setAvailableAddresses([]);
      setSelectedAddressId(null);
    }
  };

  const handleAddressChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const addressId = e.target.value ? Number(e.target.value) : null;
    setSelectedAddressId(addressId);
  };

  const resolveAddressForPassenger = (
    passenger: { id: number; addresses?: { id: number; street: string }[] }
  ): { street: string; id: number } | null => {
    const addresses = passenger.addresses ?? [];
    if (addresses.length === 0) return null;
    if (
      singleSelectedPassengerId === passenger.id &&
      selectedAddressId != null
    ) {
      const picked = addresses.find((a) => a.id === selectedAddressId);
      if (picked) return picked;
    }
    return addresses[0];
  };

  const canAddPassengers = useMemo(() => {
    if (rows.length >= MAX_BULK_PASSENGERS) return false;
    if (selectedPassengerIds.length === 0) return false;
    if (isManual) return true;
    return selectedPassengerIds.every((id) => {
      const p = passengers.find((pass) => pass.id === id);
      return (p?.addresses ?? []).length > 0;
    });
  }, [selectedPassengerIds, isManual, passengers, rows.length]);

  const handleAddPassengers = () => {
    if (rows.length >= MAX_BULK_PASSENGERS) {
      showToast(`La tabla permite máximo ${MAX_BULK_PASSENGERS} pasajeros`, "info");
      return;
    }
    if (selectedPassengerIds.length === 0) return;

    const updatedRows = [...rows];
    let added = 0;
    let skippedNoAddress = 0;

    for (const id of selectedPassengerIds) {
      if (updatedRows.length >= MAX_BULK_PASSENGERS) break;
      const passenger = passengers.find((p) => p.id === id);
      if (!passenger) continue;
      if (updatedRows.some((row) => row.id === passenger.id)) continue;

      let addressText = "MANUAL";
      let addressId: number | null = null;
      if (!isManual) {
        const address = resolveAddressForPassenger(passenger);
        if (!address) {
          skippedNoAddress += 1;
          continue;
        }
        addressText = address.street;
        addressId = address.id;
      }

      updatedRows.push({
        id: passenger.id,
        name: passengerLabel(passenger),
        phone: passenger.phone ?? "",
        address: addressText,
        address_id: addressId,
        order: updatedRows.length + 1,
      });
      added += 1;
    }

    if (added === 0) {
      if (skippedNoAddress > 0) {
        showToast("Algunos pasajeros no tienen dirección registrada", "info");
      } else {
        showToast("No se agregaron pasajeros nuevos", "info");
      }
      return;
    }

    const orderedRows = withSequentialOrder(updatedRows);
    setRows(orderedRows);
    onAddSequence(orderedRows);
    setSelectedPassengerIds([]);
    setSelectedAddressId(null);
    setAvailableAddresses([]);

    if (skippedNoAddress > 0) {
      showToast(
        `${added} agregado(s); ${skippedNoAddress} sin dirección`,
        "info"
      );
    }
  };

  const removeRow = (index: number) => {
    const updatedRows = withSequentialOrder(rows.filter((_, i) => i !== index));
    setRows(updatedRows);
    onAddSequence(updatedRows);
  };

  const moveRow = (from: number, to: number) => {
    const reordered = [...rows];
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);
    const orderedRows = withSequentialOrder(reordered);
    setRows(orderedRows);
    onAddSequence(orderedRows);
  };

  const closePassengerEdit = useCallback(() => {
    clearErrors();
    setEditingPassenger(null);
    setEditingRowIndex(null);
    setEditCompanies([]);
    setEditSavingLocked(false);
  }, [clearErrors]);

  const openPassengerEdit = useCallback(
    (row: PassengerRow, index: number) => {
      if (!row.id) {
        showToast("No se puede editar un pasajero sin identificador", "error");
        return;
      }
      clearErrors();
      editPassenger(row.id, {
        onSuccess: (resp: any) => {
          const data = resp?.data ?? {};
          const passenger = data.passenger ?? {};
          const comps = data.companies ?? [];
          const rawAddresses = (passenger.addresses ?? []).filter(
            (a: { street?: string }) => !isPlaceholderAddress(a.street ?? "")
          );
          setEditingPassenger({
            ...passenger,
            company: passenger.company_id ?? passenger.company ?? "",
            addresses: rawAddresses.length > 0 ? rawAddresses : [{ street: "" }],
          });
          setEditCompanies(
            comps.map((c: any) => ({
              value: String(c.id),
              label: c.name,
            }))
          );
          setEditingRowIndex(index);
        },
        onError: () => showToast("Error cargando datos del pasajero", "error"),
      });
    },
    [clearErrors, editPassenger]
  );

  const applyPassengerToRow = useCallback(
    (row: PassengerRow, passenger: Passenger): PassengerRow => {
      const displayName = passengerLabel(passenger);
      if (isManual) {
        return {
          ...row,
          name: displayName,
          phone: passenger.phone ?? row.phone,
        };
      }

      const addresses = (passenger.addresses ?? []).filter(
        (a) => (a.street ?? "").trim() && !isPlaceholderAddress(a.street ?? "")
      );

      let addressText = row.address;
      let addressId = row.address_id ?? null;

      const byId =
        row.address_id != null
          ? addresses.find((a) => Number(a.id) === Number(row.address_id))
          : undefined;
      const byStreet = addresses.find(
        (a) => normalizeStreet(a.street) === normalizeStreet(row.address)
      );
      const chosen = byId ?? byStreet ?? addresses[0];

      if (chosen) {
        addressText = chosen.street;
        addressId = chosen.id && chosen.id > 0 ? chosen.id : null;
      }

      return {
        ...row,
        name: displayName,
        phone: passenger.phone ?? row.phone,
        address: addressText,
        address_id: addressId,
      };
    },
    [isManual]
  );

  const savePassengerEdit = useCallback(async () => {
    if (!editingPassenger?.id) return;
    setEditSavingLocked(true);
    clearErrors();
    try {
      const cleanedAddresses = (editingPassenger.addresses ?? []).filter(
        (a) => (a.street ?? "").trim() && !isPlaceholderAddress(a.street ?? "")
      );
      if (cleanedAddresses.length === 0) {
        showToast("Debe ingresar al menos una dirección real", "error");
        return;
      }
      const { also_as_requester: _r, also_as_authorizer: _a, ...updatePayload } = {
        ...editingPassenger,
        addresses: cleanedAddresses,
      };
      return await updatePassenger(editingPassenger.id, updatePayload);
    } catch (error: any) {
      const fields = error?.response?.data;
      if (fields?.errors) {
        setErrorsFromResponse(fields.errors);
        return;
      }
      setErrorsFromResponse(fields);
    } finally {
      setEditSavingLocked(false);
    }
  }, [clearErrors, editingPassenger, setErrorsFromResponse, updatePassenger]);

  const handlePassengerEditComplete = useCallback(
    (ok: boolean, resp?: any) => {
      if (!ok) {
        setErrorsFromResponse(resp);
        const errs = resp?.errors ?? resp?.data;
        const first =
          errs && typeof errs === "object" ? Object.values(errs)[0] : null;
        const msg =
          (typeof resp?.message === "string" && resp.message) ||
          (typeof first === "string" ? first : null) ||
          "Error guardando pasajero";
        showToast(String(msg), "error");
        return;
      }

      const d = resp?.data ?? {};
      const merged: Passenger = {
        ...(editingPassenger as Passenger),
        ...d,
        addresses:
          Array.isArray(d.addresses) && d.addresses.length > 0
            ? d.addresses
            : editingPassenger?.addresses ?? [],
      };
      const displayName = passengerLabel(merged);

      if (editingRowIndex != null) {
        setRows((prev) => {
          const next = prev.map((row, i) =>
            i === editingRowIndex ? applyPassengerToRow(row, merged) : row
          );
          const ordered = withSequentialOrder(next);
          onAddSequence(ordered);
          return ordered;
        });
      }

      if (merged.id) {
        onPassengerUpdated?.({
          id: Number(merged.id),
          name: displayName,
          addresses: (merged.addresses ?? [])
            .filter(
              (a) =>
                (a.street ?? "").trim() && !isPlaceholderAddress(a.street ?? "")
            )
            .map((a) => ({
              id: Number(a.id ?? 0),
              street: a.street,
            })),
        });
      }

      showToast(resp?.message ?? "Pasajero actualizado correctamente", "success");
      closePassengerEdit();
    },
    [
      applyPassengerToRow,
      closePassengerEdit,
      editingPassenger,
      editingRowIndex,
      onAddSequence,
      onPassengerUpdated,
      setErrorsFromResponse,
    ]
  );

  const handleBulkPassengersCreated = useCallback(
    (created: BulkPassengerCreated[]) => {
      onPassengersCreated?.(created);

      setRows((prevRows) => {
        const nextRows = [...prevRows];
        let added = 0;

        for (const p of created) {
          if (nextRows.some((row) => row.id === p.id)) continue;

          const displayName = passengerLabel(p);
          let addressText = "MANUAL";
          let addressId: number | null = null;

          if (!isManual) {
            const enteredStreet = normalizeStreet(p.street ?? "");
            const addr =
              pickPreferredAddress(p.addresses, enteredStreet) ??
              (enteredStreet && !isPlaceholderAddress(enteredStreet)
                ? { id: 0, street: enteredStreet }
                : null);

            if (!addr?.street || isPlaceholderAddress(addr.street)) {
              showToast(
                `${displayName}: ingresa una dirección real en el alta masiva`,
                "info"
              );
              continue;
            }

            addressText = addr.street;
            if (addr.id > 0) {
              addressId = addr.id;
            }
          }

          nextRows.push({
            id: p.id,
            name: displayName,
            phone: p.phone ?? "",
            address: addressText,
            address_id: addressId,
            order: nextRows.length + 1,
          });
          added += 1;
        }

        if (added > 0) {
          const orderedRows = withSequentialOrder(nextRows);
          onAddSequence(orderedRows);
          showToast(
            added === 1
              ? "Pasajero agregado a la ruta"
              : `${added} pasajeros agregados a la ruta`,
            "success"
          );
          return orderedRows;
        }

        return nextRows;
      });
    },
    [isManual, onAddSequence, onPassengersCreated]
  );

  return (
    <div className="service-form-step2">
      <h3 className="form-title">Configuración de Rutas y Pasajeros</h3>

      {fetching.sameTimeServices && (
        <div className="svc-same-hour-panel svc-same-hour-panel--loading" role="status" aria-busy>
          <p className="svc-same-hour-panel__loading">
            {isEnaex && String(value.enaex_code ?? "").trim()
              ? "Comprobando misma hora y code ENAEX…"
              : "Comprobando agenda a la misma hora…"}
          </p>
        </div>
      )}

      {!fetching.sameTimeServices && sameSlotConflicts.length > 0 && (
        <div
          className="svc-same-hour-panel svc-same-hour-panel--has-conflicts"
          role="status"
        >
          <p className="svc-same-hour-panel__title">SERVICIO ENCONTRADO A MISMA HORA</p>
          {sameSlotConflicts.map((c) => (
            <div key={c.id} className="svc-same-hour-panel__block">
              <p className="svc-same-hour-panel__detail-label">DETALLE</p>
              <dl className="svc-same-hour-panel__dl">
                <div>
                  <dt>ID servicio</dt>
                  <dd>#{c.id}</dd>
                </div>
                <div>
                  <dt>Origen</dt>
                  <dd>{c.origin}</dd>
                </div>
                <div>
                  <dt>Destino</dt>
                  <dd>{c.destination}</dd>
                </div>
                <div>
                  <dt>Pasajeros</dt>
                  <dd>{c.passengers}</dd>
                </div>
              </dl>
            </div>
          ))}
          <p className="svc-same-hour-panel__hint">
            Puedes continuar: esto no impide agregar otro servicio en el mismo horario.
          </p>
        </div>
      )}

      {!fetching.sameTimeServices && isEnaex && codeConflicts.length > 0 && (
        <div
          className="svc-same-hour-panel svc-same-hour-panel--has-conflicts svc-same-hour-panel--enaex-code"
          role="status"
        >
          <p className="svc-same-hour-panel__title">CODE ENAEX YA EN USO</p>
          {codeConflicts.map((c) => (
            <div key={c.id} className="svc-same-hour-panel__block">
              <p className="svc-same-hour-panel__detail-label">DETALLE</p>
              <dl className="svc-same-hour-panel__dl">
                <div>
                  <dt>ID servicio</dt>
                  <dd>#{c.id}</dd>
                </div>
                <div>
                  <dt>Code</dt>
                  <dd>{c.code}</dd>
                </div>
                <div>
                  <dt>Origen</dt>
                  <dd>{c.origin}</dd>
                </div>
                <div>
                  <dt>Destino</dt>
                  <dd>{c.destination}</dd>
                </div>
                <div>
                  <dt>Pasajeros</dt>
                  <dd>{c.passengers}</dd>
                </div>
              </dl>
            </div>
          ))}
          <p className="svc-same-hour-panel__hint">
            Puedes continuar: otro servicio ya usa este code; revisa si corresponde duplicar el registro.
          </p>
        </div>
      )}

      <div className="svc-step2-layout">
        <div className="svc-step2-layout__toolbar">
          {isEnaex && (
            <div className="svc-step2-toolbar__enaex svc-step2-surface svc-step2-surface--compact">
              <label className="svc-enaex-code-label" htmlFor="svc-enaex-code-step2">
                Code (ENAEX)
              </label>
              <input
                id="svc-enaex-code-step2"
                type="text"
                className="svc-enaex-code-input"
                value={value.enaex_code ?? ""}
                onChange={(e) => onChange("enaex_code", e.target.value)}
                placeholder="Ej. referencia interna"
                maxLength={128}
                autoComplete="off"
              />
            </div>
          )}
          <div className="form-section svc-step2-layout__type">
            <div className="svc-step2-surface svc-step2-surface--compact">
              <div
                className="svc-type-toggle"
                role="group"
                aria-labelledby="svc-type-toggle-label"
              >
                <span className="svc-type-toggle-label" id="svc-type-toggle-label">
                  Tipo de Servicio
                </span>
                <div className="svc-type-toggle-btns">
                {(
                  [
                    {
                      label: "Destino → Domicilio",
                      value: "DESTINO_DOMICILIOS",
                    },
                    {
                      label: "Domicilio → Destino",
                      value: "ORIGEN_DOMICILIOS",
                    },
                    { label: "Manual", value: "MANUAL" },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={`svc-type-toggle-btn ${
                      serviceType === opt.value ? "is-active" : ""
                    }`}
                    aria-pressed={serviceType === opt.value}
                    onClick={() => {
                      if (serviceType !== opt.value) {
                        handleServiceTypeChange(opt.value);
                      }
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="svc-step2-layout__passengers">
        <div className="svc-passenger-card svc-step2-surface svc-step2-surface--passengers">
          <div className="svc-passenger-card__head">
            <div className="svc-passenger-card__head-text">
              <h4 className="svc-passenger-card__title">
                <Users className="svc-passenger-card__title-icon" size={22} aria-hidden />
                Pasajeros
              </h4>
              <p className="svc-passenger-card__hint">
                {isManual
                  ? "Marca de 1 a 7 pasajeros y agrégalos a la ruta (máximo total: 7)."
                  : "Marca de 1 a 7 pasajeros; en domicilio se usa la primera dirección (o la que elijas si es uno solo). Máximo total: 7."}
              </p>
            </div>
            {rows.length > 0 && (
              <span className="svc-passenger-card__badge" aria-live="polite">
                {rows.length} {rows.length === 1 ? "pasajero" : "pasajeros"}
              </span>
            )}
          </div>

          {passengers.length === 0 ? (
            <>
              <p className="svc-passenger-card__empty">
                No hay pasajeros disponibles para esta empresa. Puedes crear uno o varios con
                el botón de abajo.
              </p>
              <div className="svc-passenger-card__actions">
                <BaseButton
                  label="Agregar nuevo pasajero"
                  leftIcon={<UserRoundPlus size={18} strokeWidth={2.25} aria-hidden />}
                  className="svc-passenger-card__btn svc-passenger-card__btn--secondary"
                  onClick={() => {
                    if (!companyId) {
                      showToast("Selecciona una empresa en el paso anterior", "info");
                      return;
                    }
                    setBulkModalOpen(true);
                  }}
                  disabled={!companyId}
                />
              </div>
            </>
          ) : (
            <div className="svc-passenger-card__body">
              <div className="svc-passenger-picker">
                <div className="svc-passenger-picker__main">
                  <MultiSelectSearchable
                    className="svc-passenger-multi"
                    label="Pasajeros"
                    name="selected_passengers"
                    placeholder="Toca para buscar y marcar (1 a 7)"
                    searchPlaceholder="Buscar por nombre…"
                    preserveCase
                    sortAlphabetically
                    selectedFirst
                    menuPortal
                    maxSelections={availableSlots}
                    onMaxSelections={() =>
                      showToast(
                        availableSlots === 0
                          ? `La tabla ya alcanzó el máximo de ${MAX_BULK_PASSENGERS}`
                          : `Solo puedes seleccionar ${availableSlots} pasajero(s) más`,
                        "info"
                      )
                    }
                    value={selectedPassengerIds}
                    options={passengerSelectOptions}
                    onChange={handlePassengersChange}
                  />
                </div>

                {!isManual && singleSelectedPassengerId != null && (
                  <div className="svc-passenger-picker__address">
                    <BaseSelect
                      label="Dirección del pasajero"
                      name="address"
                      placeholder={
                        availableAddresses.length === 0
                          ? "Sin direcciones registradas"
                          : "Seleccionar dirección"
                      }
                      preserveCase
                      options={availableAddresses.map((a) => ({
                        value: String(a.id),
                        label: a.street,
                      }))}
                      value={selectedAddressId ? String(selectedAddressId) : ""}
                      onChange={handleAddressChange}
                      disabled={availableAddresses.length === 0}
                    />
                  </div>
                )}
              </div>

              <div className="svc-passenger-card__actions">
                <BaseButton
                  label="Agregar nuevo pasajero"
                  leftIcon={<UserRoundPlus size={18} strokeWidth={2.25} aria-hidden />}
                  className="svc-passenger-card__btn svc-passenger-card__btn--secondary"
                  onClick={() => {
                    if (!companyId) {
                      showToast("Selecciona una empresa en el paso anterior", "info");
                      return;
                    }
                    setBulkModalOpen(true);
                  }}
                  disabled={!companyId}
                />
                <BaseButton
                  label={
                    selectedPassengerIds.length > 1
                      ? `Agregar ${selectedPassengerIds.length} a la ruta`
                      : "Agregar a la ruta"
                  }
                  leftIcon={<UserPlus size={18} strokeWidth={2.25} aria-hidden />}
                  className="svc-passenger-card__btn"
                  onClick={handleAddPassengers}
                  disabled={!canAddPassengers}
                />
              </div>
            </div>
          )}
        </div>
        </div>

        <div className="svc-step2-layout__route">
      <div className="form-section svc-route-section svc-step2-surface svc-step2-surface--route">
        <div className="svc-route-section__header">
          <h3 className="svc-route-section__heading">Ruta y paradas</h3>
          <p className="svc-route-intro" role="status">
            {ROUTE_INTRO[serviceType] ?? ROUTE_INTRO.MANUAL}
          </p>
        </div>

        {serviceType === "DESTINO_DOMICILIOS" ? (
          <div className="svc-route-split svc-route-split--destiny">
            <LocationSelectCard
              name="origin"
              title="Origen"
              shortHint="Punto de salida del servicio (catálogo)."
              icon={<MapPin size={22} strokeWidth={2} />}
              options={locationOptions}
              value={value.origin ? String(value.origin) : ""}
              onChange={(e) => onChange("origin", e.target.value)}
              onAddNew={() => setLocationModalFor("origin")}
            />
            <div className="svc-route-table-panel">
              <PassengerServicesTable
                title="Destinos (domicilios de pasajeros)"
                rows={rows}
                onRemove={removeRow}
                passengers={[]}
                onMove={moveRow}
                onEditPassenger={openPassengerEdit}
              />
            </div>
          </div>
        ) : serviceType === "ORIGEN_DOMICILIOS" ? (
          <div className="svc-route-split svc-route-split--origin">
            <div className="svc-route-table-panel svc-route-table-panel--first">
              <PassengerServicesTable
                title="Orígenes (domicilios de pasajeros)"
                rows={rows}
                onRemove={removeRow}
                passengers={[]}
                onMove={moveRow}
                onEditPassenger={openPassengerEdit}
              />
            </div>
            <LocationSelectCard
              name="destination"
              title="Destino"
              shortHint="Punto de llegada común (catálogo)."
              icon={<MapPinned size={22} strokeWidth={2} />}
              options={locationOptions}
              value={value.destination ? String(value.destination) : ""}
              onChange={(e) => onChange("destination", e.target.value)}
              onAddNew={() => setLocationModalFor("destination")}
            />
          </div>
        ) : (
          <>
            <div className="svc-loc-grid">
              <LocationSelectCard
                name="origin"
                title="Origen"
                shortHint="Punto de partida."
                icon={<MapPin size={22} strokeWidth={2} />}
                options={locationOptions}
                value={value.origin ? String(value.origin) : ""}
                onChange={(e) => onChange("origin", e.target.value)}
                onAddNew={() => setLocationModalFor("origin")}
              />
              <LocationSelectCard
                name="destination"
                title="Destino"
                shortHint="Punto de llegada."
                icon={<MapPinned size={22} strokeWidth={2} />}
                options={locationOptions}
                value={value.destination ? String(value.destination) : ""}
                onChange={(e) => onChange("destination", e.target.value)}
                onAddNew={() => setLocationModalFor("destination")}
              />
            </div>
            <div className="svc-route-table-panel">
              <PassengerServicesTable
                title="Pasajeros en ruta"
                rows={rows}
                onRemove={removeRow}
                passengers={[]}
                onMove={moveRow}
                onEditPassenger={openPassengerEdit}
              />
            </div>
          </>
        )}
      </div>
        </div>
      </div>

      <BulkPassengerModal
        isOpen={bulkModalOpen}
        onClose={() => setBulkModalOpen(false)}
        companyId={companyId}
        companyLabel={companyName}
        existingPassengers={passengers}
        onSuccess={handleBulkPassengersCreated}
      />

      <LocationQuickAddModal
        isOpen={locationModalFor !== null}
        onClose={() => setLocationModalFor(null)}
        onCreated={handleLocationCreated}
      />

      {editingPassenger && (
        <div className="passengers-modal">
          <div className="passengers-modal-inner">
            <div className="passengers-modal-title-row">
              <h3>Editar pasajero</h3>
            </div>
            <PassengerForm
              value={editingPassenger}
              onChange={setEditingPassenger}
              companies={editCompanies}
              fieldErrors={fieldErrors}
            />
            <div
              style={{
                display: "flex",
                gap: 8,
                justifyContent: "flex-end",
                marginTop: 12,
              }}
            >
              <ButtonCancelForm
                callbackCancel={closePassengerEdit}
                locked={editSavingLocked}
                confirmCancel={false}
              />
              <ButtonSaveForm
                callbackSave={savePassengerEdit}
                locked={editSavingLocked}
                onComplete={handlePassengerEditComplete}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceFormStep2;
